import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface DictatedCourse {
  external_id: string;
  notes: string | undefined;
}

export interface TeacherProfileVariant {
  description: string;
  fullDescription: string;
  pricePerHour: number;
}

// University-first order, matching the backend's primary-profile derivation.
export const PARTICIPATION_TYPES = ['university', 'languages', 'schools'] as const;
export type ParticipationType = typeof PARTICIPATION_TYPES[number];

interface NewTeacherContextProps {
  teacher: {
    step: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    profiles: Record<string, TeacherProfileVariant>;
    busyDays: string[];
    inPersonClasses: boolean;
    inPersonClassesMessage: string;
    virtualClasses: boolean;
    virtualClassesMessage: string;
    weeksInAdvance: number;
    profilePictureBase64: string;
    dictatedCourses: Map<string, DictatedCourse[]>;
    additionalCoursesMessage: string;
    externalId: string;
  };
  updateTeacher: (teacher: Partial<NewTeacherContextProps['teacher']>) => void;
}

const defaultState = {
  teacher: {
    step: 0,
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    profiles: {} as Record<string, TeacherProfileVariant>,
    busyDays: [] as string[],
    inPersonClasses: false,
    inPersonClassesMessage: '',
    virtualClasses: true,
    virtualClassesMessage: '',
    weeksInAdvance: 4,
    profilePictureBase64: '',
    dictatedCourses: new Map<string, DictatedCourse[]>(),
    additionalCoursesMessage: '',
    externalId: ''
  },
  updateTeacher: () => {}
};

export const serializeTeacher = (teacher: NewTeacherContextProps['teacher']): string => {
  const teacherObject = {
    ...teacher,
    dictatedCourses: Array.from(teacher.dictatedCourses)
  };

  return JSON.stringify(teacherObject);
};

// Legacy drafts (before multi-profile) stored a single flat
// description/fullDescription/pricePerHour. Migrate them into a single
// `university` profile entry so in-progress signups don't break.
const migrateLegacyProfile = (teacherObject: any): Record<string, TeacherProfileVariant> => {
  if (teacherObject.profiles) {
    return teacherObject.profiles;
  }

  if (teacherObject.description === undefined) {
    return {};
  }

  return {
    university: {
      description: teacherObject.description ?? '',
      fullDescription: teacherObject.fullDescription ?? '',
      pricePerHour: teacherObject.pricePerHour ?? 0
    }
  };
};

export const deserializeTeacher = (teacherString: string): NewTeacherContextProps['teacher'] => {
  const teacherObject = JSON.parse(teacherString);

  return {
    ...teacherObject,
    profiles: migrateLegacyProfile(teacherObject),
    dictatedCourses: new Map(teacherObject.dictatedCourses)
  };
};

const NewTeacherContext = createContext<NewTeacherContextProps>(defaultState);

export const useNewTeacherContext = () => useContext(NewTeacherContext);

interface TeacherProviderProps {
  children: ReactNode;
}

export const NewTeacherProvider: React.FC<TeacherProviderProps> = ({ children }) => {
  const [teacher, setTeacher] = useState(defaultState.teacher);

  useEffect(() => {
    const storedTeacher = localStorage.getItem('teacher');

    if (storedTeacher) {
      setTeacher(deserializeTeacher(storedTeacher));
    }
  }, []);

  const updateTeacher = async (
    newUserData: Partial<NewTeacherContextProps['teacher']>
  ): Promise<void> => {
    await setTeacher((current) => {
      const mergedTeacher = { ...current, ...newUserData };

      localStorage.setItem('teacher', serializeTeacher(mergedTeacher));

      return mergedTeacher;
    });
  };

  return (
    <NewTeacherContext.Provider value={{ teacher, updateTeacher }}>
      {children}
    </NewTeacherContext.Provider>
  );
};

export default NewTeacherProvider;
